<script setup lang="ts">
withDefaults(
  defineProps<{
    loading?: boolean
    rows?: number
    active?: boolean
    variant?: 'lines' | 'table' | 'metrics' | 'button' | 'avatar'
  }>(),
  {
    loading: true,
    rows: 4,
    active: true,
    variant: 'lines'
  }
)
</script>

<template>
  <slot v-if="!loading" />
  <div
    v-else
    class="rr-skeleton"
    :class="[
      `is-${variant}`,
      { 'is-active': active }
    ]"
    aria-label="正在加载"
    aria-live="polite"
    aria-busy="true"
  >
    <template v-if="variant === 'metrics'">
      <div v-for="index in rows" :key="index" class="rr-skeleton__metric-item">
        <span class="rr-skeleton__node rr-skeleton__title" />
        <span class="rr-skeleton__node rr-skeleton__value" />
      </div>
    </template>
    <template v-else-if="variant === 'table'">
      <div v-for="index in rows" :key="index" class="rr-skeleton__row is-table">
        <span class="rr-skeleton__node" style="width: 25%" />
        <span class="rr-skeleton__node" style="width: 40%" />
        <span class="rr-skeleton__node" style="width: 20%" />
        <span class="rr-skeleton__node" style="width: 15%" />
      </div>
    </template>
    <template v-else-if="variant === 'button'">
      <span class="rr-skeleton__node rr-skeleton__button" />
    </template>
    <template v-else-if="variant === 'avatar'">
      <span class="rr-skeleton__node rr-skeleton__avatar" />
    </template>
    <template v-else>
      <div v-for="index in rows" :key="index" class="rr-skeleton__row">
        <span class="rr-skeleton__node" style="width: 38%" />
        <span class="rr-skeleton__node" style="width: 60%" />
        <span class="rr-skeleton__node" style="width: 85%" />
      </div>
    </template>
  </div>
</template>

<style scoped>
.rr-skeleton {
  display: grid;
  width: 100%;
  box-sizing: border-box;
  gap: 12px;
}

.rr-skeleton__node {
  display: inline-block;
  height: 14px;
  border-radius: 4px;
  background: var(--rr-color-surface-subtle);
}

.rr-skeleton.is-active .rr-skeleton__node {
  background: linear-gradient(90deg, var(--rr-color-surface-subtle) 25%, var(--rr-color-border) 37%, var(--rr-color-surface-subtle) 63%);
  background-size: 400% 100%;
  animation: rr-skeleton-loading 1.4s ease infinite;
}

.rr-skeleton__row {
  display: flex;
  align-items: center;
  gap: 12px;
  height: 38px;
}

.rr-skeleton__row.is-table {
  border-bottom: 1px solid var(--rr-color-border);
  padding: 0 8px;
}

.rr-skeleton.is-metrics {
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 16px;
}

.rr-skeleton__metric-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
}

.rr-skeleton__title {
  width: 50%;
  height: 12px;
}

.rr-skeleton__value {
  width: 75%;
  height: 24px;
}

.rr-skeleton__button {
  width: 80px;
  height: 32px;
  border-radius: var(--rr-radius-control);
}

.rr-skeleton__avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
}

@keyframes rr-skeleton-loading {
  0% { background-position: 100% 50%; }
  100% { background-position: 0 50%; }
}

@media (prefers-reduced-motion: reduce) {
  .rr-skeleton.is-active .rr-skeleton__node {
    animation: none;
  }
}
</style>
